/**
 * Cross-tab / multi-client live history sync helpers (T-035).
 *
 * Uses BroadcastChannel when available so other Pulse tabs refresh the same
 * ticket's investigation list immediately after a local change. Polling +
 * visibility refresh in useTicketInvestigations cover other clients / devices.
 */

export const SRE_HISTORY_SYNC_CHANNEL = 'pulse-sre-investigation-history';

/** Default poll interval while a ticket history hook is mounted (live mode). */
export const DEFAULT_HISTORY_POLL_MS = 12_000;

/** Coalesce rapid poll/focus/broadcast refresh triggers. */
export const HISTORY_REFRESH_DEBOUNCE_MS = 400;

export type HistorySyncMessage = {
  type: 'history_changed';
  ticketNumber: string;
  at: number;
  /** Publisher tab id — subscribers ignore their own publishes. */
  originId?: string;
};

function normalizeTicket(ticketNumber: string | null | undefined): string {
  return (ticketNumber || '').trim();
}

let tabOriginId: string | null = null;

export function getHistorySyncOriginId(): string {
  if (tabOriginId) return tabOriginId;
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      tabOriginId = crypto.randomUUID();
    } else {
      tabOriginId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
  } catch {
    tabOriginId = `tab_${Date.now()}`;
  }
  return tabOriginId;
}

export function publishHistorySync(ticketNumber: string): void {
  const number = normalizeTicket(ticketNumber);
  if (!number || typeof BroadcastChannel === 'undefined') return;

  try {
    const channel = new BroadcastChannel(SRE_HISTORY_SYNC_CHANNEL);
    const message: HistorySyncMessage = {
      type: 'history_changed',
      ticketNumber: number,
      at: Date.now(),
      originId: getHistorySyncOriginId(),
    };
    channel.postMessage(message);
    channel.close();
  } catch {
    /* BroadcastChannel unavailable or blocked — polling still covers sync */
  }
}

/**
 * Subscribe to history-changed events for a ticket.
 * Returns an unsubscribe function. Ignores messages from this same tab.
 */
export function subscribeHistorySync(
  ticketNumber: string,
  onSync: () => void
): () => void {
  const number = normalizeTicket(ticketNumber);
  if (!number || typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }

  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(SRE_HISTORY_SYNC_CHANNEL);
  } catch {
    return () => undefined;
  }

  const selfId = getHistorySyncOriginId();
  const handler = (event: MessageEvent<HistorySyncMessage>) => {
    const data = event?.data;
    if (!data || data.type !== 'history_changed') return;
    if (normalizeTicket(data.ticketNumber) !== number) return;
    if (data.originId && data.originId === selfId) return;
    onSync();
  };

  channel.addEventListener('message', handler);
  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
}
